import React from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Props as XAxisProps } from "recharts/types/cartesian/XAxis";
import { Props as YAxisProps } from "recharts/types/cartesian/YAxis";

import { getNumericValue } from "@/utils/get-numeric-value";
import {
  CustomTooltip,
  CustomXAxisTick,
  CustomYAxisTick,
} from "@/utils/recharts-widgets";
import { HideInfo } from "@/utils/recharts-widgets/CustomTooltip";

import { StackAreaLineChartProps } from "./StackAreaLineChartProps";
import { handleStackAreaData } from "./utils";

const legendFormatter = (
  value: string,
  legendLabelColor: string,
  hideInfo?: HideInfo
) => {
  if (hideInfo?.[value]) return <></>;
  if (value.endsWith("_stack")) value = value.replace("_stack", "");
  return (
    <span style={{ color: legendLabelColor, userSelect: "none" }}>{value}</span>
  );
};

function StackAreaLineChart({
  syncId = 0,
  data,
  linesInfo,
  stackAreasInfo,
  showGridVertical = false,
  showGridHorizontal = true,
  gridStroke = "#aaaaaa",
  gridStrokeDasharray = "1 0",
  gridStrokeWidth = 1,
  xTicksPaddingTop = 0.5,
  xTicksFormatter,
  yTicksFormatter,
  yTicksPaddingRight = 20,
  axisTickColor = "#AEAEAE",
  yAxisWidth = 50,
  xPaddingLeft = 0,
  xPaddingRight = 0,
  yPaddingTop = 0,
  legendLabelColor = "#555",
  tooltipActive = true,
  labelToHide,
  tooltipIconType,
  tooltipReverseOrder,
}: StackAreaLineChartProps) {
  if (!data.length) return <></>;

  const [processedData, processedStackInfo] = handleStackAreaData(
    data,
    stackAreasInfo
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={processedData} syncId={syncId}>
        <CartesianGrid
          vertical={showGridVertical}
          horizontal={showGridHorizontal}
          stroke={gridStroke}
          strokeDasharray={gridStrokeDasharray}
          strokeWidth={gridStrokeWidth}
        />
        <XAxis
          dataKey="name"
          padding={{ left: xPaddingLeft, right: xPaddingRight }}
          tickLine={false}
          tick={(props: XAxisProps) => (
            <CustomXAxisTick
              {...{
                props,
                xTicksPaddingTop,
                xTicksFormatter,
                axisTickColor,
              }}
            />
          )}
        />
        <YAxis
          width={yAxisWidth}
          padding={{ top: yPaddingTop }}
          tickLine={false}
          tick={(props: YAxisProps) => (
            <CustomYAxisTick
              {...{
                props,
                yTicksPaddingRight,
                yTicksFormatter,
                axisTickColor,
              }}
            />
          )}
        />
        {Object.keys(processedStackInfo)
          .filter((key) => key.endsWith("_stack"))
          .map((key) => {
            return (
              <Area
                isAnimationActive={false}
                key={key}
                dataKey={key}
                stackId={0}
                fill={processedStackInfo[key].fill}
                fillOpacity={0.35}
                strokeWidth={2}
                activeDot={false}
                stroke={processedStackInfo[key].fill}
              />
            );
          })}
        {Object.keys(linesInfo).map((key) => {
          return (
            <Line
              isAnimationActive={false}
              key={key}
              type={linesInfo[key]?.lineType}
              dataKey={key}
              stroke={linesInfo[key]?.lineStroke}
              strokeWidth={linesInfo[key]?.lineStrokeWidth}
              strokeDasharray={linesInfo[key]?.lineStrokeDasharray}
              activeDot={false}
              dot={false}
            />
          );
        })}
        <Legend
          iconType="square"
          verticalAlign="bottom"
          align="center"
          wrapperStyle={{ paddingTop: 12 }}
          formatter={(val: string) =>
            legendFormatter(val, legendLabelColor, labelToHide)
          }
          content={(props) => {
            const items = props?.payload ?? [];
            return (
              <div className="flex flex-wrap justify-center gap-2 text-xs sm:text-sm">
                {items
                  .filter(
                    (entry) => labelToHide?.[entry.value as string] !== "all"
                  )
                  .sort((a, b) => {
                    const n1 = getNumericValue((a.value ?? "") as string);
                    const n2 = getNumericValue((b.value ?? "") as string);
                    return n2 - n1;
                  })
                  .map((entry, index) => (
                    <div key={`item-${index}`} className="flex items-center">
                      <div
                        className="h-4 w-4 rounded-[8px]"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="ml-2">
                        {(entry.value as string).replace("_stack", "")}
                      </span>
                    </div>
                  ))}
              </div>
            );
          }}
        />
        <Tooltip
          wrapperStyle={{ outline: "none" }}
          content={
            tooltipActive ? (
              <CustomTooltip
                valueFormatter={yTicksFormatter}
                hideInfo={labelToHide}
                iconType={tooltipIconType}
                reverseOrder={tooltipReverseOrder}
              />
            ) : (
              <></>
            )
          }
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export default React.memo(StackAreaLineChart);
